import type { Graphics } from 'pixi.js';
import { CHALLENGE_IDS, type ChallengeId } from '../../data/full/festival';
import { BOARD } from '../../data/full/path-layout';
import { isOneOf } from '../../shared/guards';
import { clamp01 } from '../../shared/scalar';
import type { FestivalChallenge, SimState } from '../../sim/state';
import type { RenderContext } from '../context';
import type { Frame } from '../frame';
import { parseHexColor } from '../palette';
import { lookup } from '../util';
import { PATH_COLORS } from './path-way';

// The board of a challenge: three planks nailed between two posts, painted night blue by hand, a
// lantern on a post. Taken, it spins on its pins and shows its face repainted in magenta; dropped,
// it spins back.
const WIDTH = 2 * BOARD.half;
export const HEIGHT = 72;
const WOOD = parseHexColor('#c49a5c');
const DARK_WOOD = parseHexColor('#3a2418');
const POST = parseHexColor('#7a5234');
const NIGHT_PAINT = parseHexColor('#1b1d46');
export const LANTERN = parseHexColor('#ffb357');
export const PIN = parseHexColor('#a8a0a0');
const PASTILLE = { radius: 6, gap: 17, y: 23 };
// Beats of the spin, and turns of it: it ends on the painted back.
const SPIN = { beats: 0.9, halfTurns: 3 };
export const BURST_BEATS = 0.8;

export const kindOf = (id: string): ChallengeId => (isOneOf(id, CHALLENGE_IDS) ? id : 'rapides');

// The board's turn, beats after it was taken: 0 faces front, a multiple of PI shows the back.
export function boardTurn(beats: number | undefined, calm: boolean): number {
  if (beats === undefined) {
    return 0;
  }
  if (calm) {
    return Math.PI;
  }
  const k = clamp01(beats / SPIN.beats);
  return Math.PI * SPIN.halfTurns * (1 - (1 - k) ** 2.4);
}

// The board's turn, beats after a pastille put out dropped it: it spins back onto its front.
export function boardReturn(beats: number, calm: boolean): number {
  return calm ? 0 : Math.PI * SPIN.halfTurns - boardTurn(beats, false);
}

interface MaskColors {
  readonly body: number;
  readonly rim: number;
}

function mask(g: Graphics, x: number, y: number, r: number, { body, rim }: MaskColors): void {
  g.roundRect(x - r, y - r * 1.08, 2 * r, 2.16 * r, r * 0.78)
    .fill(body)
    .stroke({ width: 1.6, color: rim });
  g.moveTo(x - r * 0.62, y - r * 0.4)
    .lineTo(x - r * 0.2, y - r * 0.16)
    .moveTo(x + r * 0.62, y - r * 0.4)
    .lineTo(x + r * 0.2, y - r * 0.16)
    .stroke({ width: 1.8, color: rim, cap: 'round' });
}

// The picto of a challenge, centred on (x, y), about 26 by 18 at scale 1.
export function picto(
  g: Graphics,
  kind: ChallengeId,
  x: number,
  y: number,
  s: number,
  colors: MaskColors & { readonly crown: number },
): void {
  if (kind === 'rapides') {
    for (let i = 0; i < 3; i++) {
      g.moveTo(x + (-14 + (i % 2) * 2) * s, y + (-4 + i * 4) * s).lineTo(
        x - 5 * s,
        y + (-4 + i * 4) * s,
      );
    }
    g.stroke({ width: 2 * s, color: colors.rim, cap: 'round' });
    mask(g, x + 4 * s, y, 6.5 * s, colors);
  } else if (kind === 'nombreuses') {
    mask(g, x - 7 * s, y + 1.5 * s, 4.6 * s, colors);
    mask(g, x + 7 * s, y + 1.5 * s, 4.6 * s, colors);
    mask(g, x, y - s, 5.2 * s, colors);
  } else {
    mask(g, x, y + 2.5 * s, 6.8 * s, colors);
    const c = s * 0.85;
    g.poly(
      [-5, 2, -5.5, -4, -2.5, -1, 0, -5.5, 2.5, -1, 5.5, -4, 5, 2].map((v, i) =>
        i % 2 === 0 ? x + v * c : y - 6.5 * s + v * c,
      ),
    )
      .fill(colors.crown)
      .stroke({ width: 1, color: PATH_COLORS.outline });
  }
}

export function drawPosts(g: Graphics): void {
  const top = -HEIGHT / 2 - 6;
  const foot = BOARD.lift;
  for (const side of [-1, 1]) {
    const x = side * (BOARD.half + BOARD.post / 2);
    g.ellipse(x, foot, 5, 2).fill({ color: 0x000000, alpha: 0.35 });
    g.poly([x - 3, top + 3, x, top, x + 3, top + 3, x + 3, foot, x - 3, foot])
      .fill(POST)
      .stroke({ width: 1.5, color: PATH_COLORS.outline });
  }
  // The lantern's hook on the right post.
  const hookX = BOARD.half + BOARD.post / 2;
  g.moveTo(hookX, top + 4)
    .lineTo(hookX + 7, top + 4)
    .stroke({ width: 1.8, color: DARK_WOOD });
}

// One face of the board, centred on (0, 0): the planks, the paint brushed over them, the picto.
export function drawFace(g: Graphics, kind: ChallengeId, back: boolean, frame: Frame): void {
  const { palette } = frame;
  const plank = HEIGHT / 3;
  for (let i = 0; i < 3; i++) {
    const off = [1.2, -1.6, 0.6][i] ?? 0;
    const x = -WIDTH / 2 + off;
    const y = -HEIGHT / 2 + i * plank;
    g.roundRect(x, y, WIDTH, plank - 0.8, 1.6)
      .fill(WOOD)
      .stroke({ width: 1.8, color: PATH_COLORS.outline });
    g.moveTo(x + 3, y + plank * 0.55)
      .bezierCurveTo(
        x + WIDTH * 0.3,
        y + plank * 0.4,
        x + WIDTH * 0.6,
        y + plank * 0.7,
        x + WIDTH - 3,
        y + plank * 0.5,
      )
      .stroke({ width: 0.8, color: DARK_WOOD, alpha: 0.5 });
    for (const nail of [x + 3, x + WIDTH - 3]) {
      g.circle(nail, y + plank / 2 - 0.4, 1.1).fill(0x2a2420);
    }
  }
  const coat = back ? palette.mage : NIGHT_PAINT;
  const left = -WIDTH / 2 + 7;
  const coatWidth = WIDTH - 14;
  g.roundRect(left, -HEIGHT / 2 + 3, coatWidth, HEIGHT - 6, 3).fill(coat);
  // Two drips under the coat.
  for (const [share, length] of [
    [0.22, 4],
    [0.71, 2.5],
  ] as const) {
    const dx = left + coatWidth * share;
    g.roundRect(dx - 1, HEIGHT / 2 - 4, 2, length, 1).fill(coat);
    g.circle(dx, HEIGHT / 2 - 4 + length, 1.4).fill(coat);
  }
  picto(g, kind, 0, -8, 1.45, {
    body: back ? 0x2a1030 : palette.badVibe,
    rim: back ? 0xffffff : palette.badVibeRim,
    crown: palette.or,
  });
}

export function drawPastilles(
  g: Graphics,
  board: FestivalChallenge,
  state: SimState,
  ctx: RenderContext,
  frame: Frame,
): void {
  const count = state.players.length;
  state.players.forEach((player, slot) => {
    const x = (slot - (count - 1) / 2) * PASTILLE.gap;
    if (board.lit[slot] === true) {
      // A white rim between two dark ones: the player's colour reads on the magenta back too.
      g.circle(x, PASTILLE.y, PASTILLE.radius + 3).fill(PATH_COLORS.outline);
      g.circle(x, PASTILLE.y, PASTILLE.radius + 1.8).fill(0xffffff);
      g.circle(x, PASTILLE.y, PASTILLE.radius + 0.6).fill(PATH_COLORS.outline);
      g.circle(x, PASTILLE.y, PASTILLE.radius).fill(
        frame.palette[lookup(ctx.classTokens, player.classId, 'class')],
      );
    } else {
      g.circle(x, PASTILLE.y, PASTILLE.radius).stroke({
        width: 1.6,
        color: frame.palette.texte,
        alpha: 0.6,
      });
    }
  });
}

export function star(g: Graphics, x: number, y: number, r: number, color: number): void {
  const points: number[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const d = i % 2 === 1 ? r * 0.45 : r;
    points.push(x + Math.cos(a) * d, y + Math.sin(a) * d);
  }
  g.poly(points).fill(color).stroke({ width: 1, color: PATH_COLORS.outline });
}
